import { type ReactNode } from 'react'
import { useNavigate } from 'react-router'

/**
 * Общая shell для 3-step polls-onboarding wizard'а (design_v2_pool/onboarding.html
 * slide 18/19/20). Header со step-counter + cancel; footer с back/next.
 *
 * Cancel: →/polls (§ решение Виктора 2026-07-27, тип 3 flow).
 */
export function PollsWizardShell({
  step,
  total,
  showCancel = true,
  children,
  footer,
}: {
  step: number
  total: number
  showCancel?: boolean
  children: ReactNode
  footer?: ReactNode
}) {
  const navigate = useNavigate()

  return (
    <div className="polls-wizard">
      <header className="pw-top">
        <div className="pw-brand">
          <img src="/assets/tgx-logo.svg" alt="TeleGraphyx" className="pw-logo-img" />
          <span className="pw-eyebrow">Опрос</span>
        </div>
        <div className="pw-top-spacer" />
        <span className="pw-step">
          шаг <b>{step}</b> из <b>{total}</b>
        </span>
        {showCancel && (
          <button className="pw-cancel" type="button" onClick={() => navigate('/polls')}>
            Отменить настройку
          </button>
        )}
      </header>

      <main className="pw-main">{children}</main>

      {footer && <footer className="pw-foot">{footer}</footer>}

      <ShellStyles />
    </div>
  )
}

function ShellStyles() {
  return (
    <style>{`
      .polls-wizard {
        min-height: 100vh;
        display: grid; grid-template-rows: auto 1fr auto;
        background: var(--tg-bg, #fafafd);
      }
      .pw-top {
        display: flex; align-items: center; gap: 16px;
        padding: 16px 32px; border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        background: var(--tg-surface, #fff);
        box-sizing: border-box;
      }
      .pw-brand { display: inline-flex; align-items: center; gap: 12px; }
      .pw-logo-img { height: 26px; width: auto; display: block; }
      .pw-eyebrow {
        font-weight: 700; font-size: 11px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--tg-mute, #8f9eb0);
      }
      .pw-top-spacer { flex: 1; }
      .pw-step {
        font-size: 12.5px; color: var(--tg-mute, #8f9eb0); font-weight: 500;
      }
      .pw-step b {
        color: var(--tg-ink, #111); font-weight: 700; font-variant-numeric: tabular-nums;
      }
      .pw-cancel {
        border: 0; background: transparent; color: var(--tg-mute, #8f9eb0);
        font-family: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer;
        padding: 4px 0;
      }
      .pw-cancel:hover { color: var(--tg-ink-2, #333); }

      .pw-main {
        max-width: 1120px; margin: 0 auto; width: 100%;
        padding: 40px 32px 112px;
        box-sizing: border-box;
      }

      .pw-foot {
        display: flex; align-items: center; gap: 12px;
        padding: 16px 32px;
        border-top: 1px solid var(--tg-line-2, #f0f0f4);
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 20;
        background: rgba(255,255,255,0.92);
        backdrop-filter: blur(12px);
        box-sizing: border-box;
      }
      .pw-foot .pw-foot-spacer { flex: 1; }

      .pw-btn {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 11px 18px; border-radius: 10px;
        font-family: inherit; font-weight: 600; font-size: 14px;
        cursor: pointer; border: 1px solid transparent; white-space: nowrap;
        text-decoration: none;
      }
      .pw-btn svg { width: 14px; height: 14px; }
      .pw-btn-primary {
        background: var(--tg-blue, #0066ff); color: white;
        box-shadow: 0 1px 2px rgba(0,102,255,0.18);
      }
      .pw-btn-primary:hover:not(:disabled) { background: var(--tg-blue-hover, #0052cc); }
      .pw-btn-primary:disabled { background: var(--tg-mute-2, #c4c4c8); cursor: default; box-shadow: none; }
      .pw-btn-ghost {
        background: var(--tg-surface, #fff);
        color: var(--tg-ink-2, #333);
        border-color: var(--tg-line-2, #f0f0f4);
      }
      .pw-btn-ghost:hover { background: var(--tg-surface-2, #f5f5f9); color: var(--tg-ink, #111); }

      @media (max-width: 700px) {
        .pw-top, .pw-foot { padding-left: 16px; padding-right: 16px; }
        .pw-top {
          gap: 10px;
          flex-wrap: wrap;
          align-items: flex-start;
        }
        .pw-brand {
          gap: 8px;
          min-width: 0;
          flex: 1 1 auto;
        }
        .pw-logo-img { height: 22px; }
        .pw-eyebrow { font-size: 10px; letter-spacing: 0.08em; }
        .pw-top-spacer { display: none; }
        .pw-step {
          order: 3;
          width: 100%;
          font-size: 12px;
        }
        .pw-cancel {
          margin-left: auto;
          text-align: right;
          white-space: normal;
          line-height: 1.2;
          max-width: 96px;
          padding-top: 2px;
        }
        .pw-main { padding: 28px 16px 108px; }
        .pw-foot {
          flex-wrap: nowrap;
          align-items: center;
          padding-top: 12px;
          padding-bottom: 12px;
        }
        .pw-foot .pw-foot-spacer {
          display: block;
          flex: 1;
          min-width: 8px;
        }
        .pw-btn {
          flex: 0 1 auto;
          min-width: 0;
          justify-content: center;
          white-space: nowrap;
          text-align: center;
          padding: 9px 12px;
          font-size: 12.5px;
        }
        .pw-btn-primary { flex: 1 1 auto; }
      }
    `}</style>
  )
}
